/**
 * What the grid paints, read from the publish check and nothing else.
 *
 * The colour on a cell has exactly one source: the `publish_check` report the
 * pre-publish panel already prints, which is itself computed from
 * `_shared/schedule-invariants.ts`. There is no second opinion computed in the
 * browser — a grid that disagrees with the panel would be worse than no colour.
 *
 * An error entry names one or more games (a clash names two); every game it
 * names is red. A warning group carries items; every item is yellow. Red wins.
 */

export type GameIssue = { errors: string[]; warnings: string[] };
export type IssueMap = Record<string, GameIssue>;

const line = (title: string | null | undefined, message: string | null | undefined) =>
  [String(title ?? '').trim(), String(message ?? '').trim()].filter(Boolean).join(': ');

/** gameId → the publish check's own words, split into errors and warnings. */
export function buildIssueMap(check: any): IssueMap {
  const map: IssueMap = {};
  const at = (id: string) => (map[id] ??= { errors: [], warnings: [] });

  for (const e of check?.errors ?? []) {
    const text = line(e?.title, e?.message);
    for (const g of e?.games ?? []) {
      if (!g?.game_id) continue;
      const bucket = at(String(g.game_id)).errors;
      if (!bucket.includes(text)) bucket.push(text);
    }
  }

  for (const w of check?.warnings ?? []) {
    const groupText = line(w?.title, w?.note);
    for (const item of w?.items ?? []) {
      const rows = Array.isArray(item?.games)
        ? item.games.filter((g: any) => g?.game_id)
        : item?.game_id ? [item] : [];
      for (let index = 0; index < rows.length; index += 1) {
        const row = rows[index];
        let text = String(item?.message ?? '').trim() || groupText;
        if (w?.kind === 'back_to_back' && rows.length === 2) {
          const other = rows[index === 0 ? 1 : 0];
          const otherLabel = other?.game_number != null
            ? `Game ${other.game_number}`
            : String(other?.label ?? 'the other game');
          const gap = Number(item?.gap_minutes);
          const direction = index === 0 ? 'later' : 'earlier';
          text = `Back to back with ${otherLabel}${Number.isFinite(gap) ? `, ${gap} min ${direction}` : ''}.`;
        }
        const bucket = at(String(row.game_id)).warnings;
        if (!bucket.includes(text)) bucket.push(text);
      }
    }
  }

  return map;
}

/** 'error' beats 'warning' beats nothing. */
export function issueLevel(issue: GameIssue | undefined | null): 'error' | 'warning' | null {
  if (issue?.errors?.length) return 'error';
  if (issue?.warnings?.length) return 'warning';
  return null;
}
